import { alpha, Theme } from '@mui/material/styles'
import type useBatchRegenerate from '../../../hooks/useBatchRegenerate'
import type { BatchCells, BatchDecimals, CellsMode, FilterOverrideMode, OverrideMode } from '../../../hooks/useBatchRegenerate'

/** What `useBatchRegenerate` returns: the wizard reads and sets it, never copies it. */
export type BatchRegenerateState = ReturnType<typeof useBatchRegenerate>

/** The Data Viz "My DataViz" look (TierSection variant `mine`): a light info
 *  tint with an info edge, on a white page. */
export const blueBoxSx = {
  backgroundColor: (theme: Theme) => alpha(theme.palette.info.main, 0.05),
  borderLeft: (theme: Theme) => `4px solid ${theme.palette.info.main}`,
  borderRadius: 1,
  p: 2,
}

/** "My Tables / Sky / Wave1" from a folder path inside My Tables. */
export const myTablesPath = (folder: string) => ['My Tables', ...folder.split('/').filter(Boolean)].join(' / ')

export const describeTop = (mode: OverrideMode, value: string) =>
  mode === 'replace' ? (value ? `All top specs will be replaced with: ${value}` : 'Choose the variables for the new top.')
  : mode === 'remove' ? 'Top specs are removed from every table.'
  : 'Every table keeps its own top spec.'

export const describeFilter = (mode: FilterOverrideMode, value: string) =>
  mode === 'replace' ? (value ? `All filters will be replaced with: ${value}` : 'Build the new filter.')
  : mode === 'append' ? (value ? `Each table's filter is ANDed with: ${value}` : 'Build the filter to add.')
  : mode === 'remove' ? 'Filters are removed from every table.'
  : 'Every table keeps its own filter.'

export const describeWeight = (mode: OverrideMode, value: string) =>
  mode === 'replace' ? (value ? `All weights will be replaced with: ${value}` : 'Choose a weight variable.')
  : mode === 'remove' ? 'Weights are removed from every table.'
  : 'Every table keeps its own weight.'

export const describeCaseFilter = (mode: OverrideMode, value: string) =>
  mode === 'replace' ? (value ? `All case filters will be replaced with: ${value}` : 'Choose a variable and its codes.')
  : mode === 'remove' ? 'Case filters are removed from every table.'
  : 'Every table keeps its own case filter.'

export const describeCells = (mode: CellsMode, cells: BatchCells) => {
  if (mode !== 'set') return 'Every table keeps its own cell display.'
  const on = [cells.frequencies && 'Frequencies', cells.columnPercents && 'Column %', cells.rowPercents && 'Row %'].filter(Boolean)
  return on.length > 0 ? `Every table shows: ${on.join(', ')}` : 'Turn on at least one.'
}

export const describeDecimals = (mode: CellsMode, decimals: BatchDecimals, invalid: boolean) =>
  mode !== 'set' ? 'Every table keeps its own decimal places.'
  : invalid ? 'Use a whole number from 0 to 9.'
  : `Every table shows Frequencies to ${decimals.frequencies} and Percents to ${decimals.percents} decimal places.`

/** What still blocks Next on "What would you like to change?", in plain words. */
export const changesMissing = (batch: BatchRegenerateState): string[] => {
  const missing: string[] = []
  if (batch.topMode === 'replace' && !batch.topValue.trim()) missing.push('choose the top variables')
  if ((batch.filterMode === 'replace' || batch.filterMode === 'append') && !batch.filterValue.trim()) missing.push('build the filter')
  if (batch.weightMode === 'replace' && !batch.weightValue.trim()) missing.push('choose a weight')
  if (batch.caseFilterMode === 'replace' && !batch.caseFilterValue.trim()) missing.push('choose a case filter')
  if (batch.cellsInvalid) missing.push('turn on at least one cell value')
  if (batch.decimalsInvalid) missing.push('use decimal places from 0 to 9')
  return missing
}
